<template>
  <Teleport to="body">
    <div class="email-registration__backdrop" @keydown.esc="emit('close')">
      <section
        class="email-registration"
        role="dialog"
        aria-modal="true"
        aria-labelledby="miniapp-email-title"
        :aria-busy="busy"
      >
        <h3 id="miniapp-email-title">{{ t('miniApp.email.title') }}</h3>
        <p
          v-if="flow.screen === 'protected_account'"
          class="email-registration__hint"
        >
          {{ t('miniApp.email.protectedAccount') }}
        </p>
        <p
          v-else-if="flow.screen === 'confirm_email'"
          class="email-registration__hint"
        >
          {{ t('tmaEmail.savedHint') }}
          <strong>{{ flow.email }}</strong>
        </p>
        <p v-else-if="flow.screen === 'code'" class="email-registration__hint">
          {{ t('miniApp.email.codeHint') }}
          <strong v-if="flow.email">{{ flow.email }}</strong>
        </p>
        <p v-else-if="flow.screen === 'email'" class="email-registration__hint">
          {{ t('miniApp.email.emailHint') }}
        </p>
        <p v-else class="email-registration__hint">
          {{ t('miniApp.email.recoveryHint') }}
        </p>

        <BaseButton
          v-if="flow.screen === 'confirm_email' && flow.revision"
          monochrome
          block
          size="lg"
          data-action="send_code"
          :loading="busy"
          @click="emit('action', 'send_code')"
          >{{ t('tmaEmail.sendCode') }}</BaseButton
        >
        <form v-if="canInput" @submit.prevent="submit">
          <BaseInput
            :label="
              flow.screen === 'code'
                ? t('miniApp.email.codeLabel')
                : t('miniApp.email.emailLabel')
            "
            id="miniapp-email-input"
            ref="inputRef"
            v-model="draft"
            :type="flow.screen === 'code' ? 'text' : 'email'"
            :inputmode="flow.screen === 'code' ? 'numeric' : 'email'"
            :autocomplete="flow.screen === 'code' ? 'one-time-code' : 'email'"
            :maxlength="flow.screen === 'code' ? 6 : 254"
            :pattern="flow.screen === 'code' ? '[0-9]{6}' : undefined"
            :disabled="busy"
            :aria-invalid="!!displayError"
            :aria-describedby="displayError ? 'miniapp-email-error' : undefined"
            autocapitalize="none"
            :spellcheck="false"
            required
          />
          <BaseButton
            type="submit"
            monochrome
            block
            size="lg"
            :loading="busy"
            :disabled="!valid"
          >
            {{
              flow.screen === 'email'
                ? t('tmaEmail.sendCode')
                : t('common.continue')
            }}
          </BaseButton>
        </form>

        <p
          v-if="displayError"
          id="miniapp-email-error"
          class="email-registration__error"
          role="alert"
        >
          {{ displayError }}
        </p>
        <div class="email-registration__actions">
          <BaseButton
            monochrome
            variant="ghost"
            block
            v-if="flow.screen === 'code' && flow.revision"
            data-action="resend"
            :disabled="busy"
            @click="emit('action', 'resend')"
            >{{ t('miniApp.email.resend') }}</BaseButton
          >
          <BaseButton
            v-if="flow.revision && flow.screen !== 'email'"
            monochrome
            variant="ghost"
            block
            data-action="change_email"
            @click="emit('action', 'change_email')"
            >{{ t('miniApp.email.change') }}</BaseButton
          >
          <BaseButton
            v-if="flow.revision && flow.changing_email"
            monochrome
            variant="ghost"
            block
            data-action="cancel_change"
            @click="emit('action', 'cancel_change')"
            >{{ t('common.back') }}</BaseButton
          >
          <BaseButton
            v-if="flow.revision && flow.can_skip === true"
            monochrome
            variant="ghost"
            block
            data-action="skip"
            :disabled="busy"
            @click="emit('action', 'skip')"
            >{{ t('tmaEmail.skip') }}</BaseButton
          >
          <BaseButton
            monochrome
            variant="ghost"
            block
            data-action="cancel"
            @click="emit('close')"
            >{{ t('miniApp.email.cancel') }}</BaseButton
          >
        </div>
      </section>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseButton from '@/shared/components/base/BaseButton.vue'
import BaseInput from '@/shared/components/base/BaseInput.vue'
import type {
  TelegramEmailAction,
  TelegramEmailFlow,
} from './TelegramAuthService'
import { emailErrorKey } from './tmaAuthFlow'

const props = defineProps<{
  flow: TelegramEmailFlow
  busy: boolean
  error: string
}>()
const emit = defineEmits<{
  (event: 'submit', text: string): void
  (event: 'action', action: Exclude<TelegramEmailAction, 'cancel'>): void
  (event: 'close'): void
}>()
const { t } = useI18n()
const draft = ref('')
const inputRef = ref<InstanceType<typeof BaseInput> | null>(null)
const canInput = computed(
  () => props.flow.screen === 'email' || props.flow.screen === 'code',
)
const valid = computed(() =>
  props.flow.screen === 'code'
    ? /^\d{6}$/.test(draft.value.trim())
    : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.value.trim()),
)
const displayError = computed(
  () =>
    props.error || (props.flow.error ? t(emailErrorKey(props.flow.error)) : ''),
)
function submit() {
  if (valid.value && !props.busy && canInput.value)
    emit('submit', draft.value.trim())
}
watch(
  () => [props.flow.screen, props.flow.revision],
  () => {
    draft.value = ''
    nextTick(() => inputRef.value?.focus())
  },
  { immediate: true },
)
</script>

<style scoped lang="scss">
.email-registration__backdrop {
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: var(--ds-scrim);
  backdrop-filter: blur(4px);
}
.email-registration {
  box-sizing: border-box;
  width: min(420px, 100%);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  padding: 24px;
  border-radius: var(--ds-radius-modal);
  background: var(--ds-text-on-accent);
  box-shadow: 0 24px 48px var(--ds-scrim);
  color: var(--hm-text-primary);
  h3 {
    margin: 0 0 16px;
    text-align: center;
    font-size: var(--ds-text-size-h-sm);
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}
.email-registration__hint {
  margin: 0 0 20px;
  text-align: center;
  line-height: 1.5;
  color: var(--hm-text-secondary);
  strong {
    display: block;
    overflow-wrap: anywhere;
  }
}
.email-registration__error {
  color: var(--hm-danger);
  line-height: 1.5;
  font-size: var(--p-text-13);
}
.email-registration__actions {
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  gap: 8px;
}
</style>
